import React, { useState, useEffect } from 'react';
import {
  Container,
  Heading,
  Box,
  Flex,
  SimpleGrid,
  VStack,
  Button,
  Text,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Alert,
  AlertIcon,
  useToast,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { FaCoins, FaChartLine, FaUsers, FaMoneyBillWave } from 'react-icons/fa';
import { useWallet } from '../context/WalletContext';
import StatsCard from '../components/StatsCard';
import TransactionList from '../components/TransactionList';
import liquidityService, { LiquidityProvider } from '../services/liquidityService';
import blindboxService, { PoolInfo, Transaction } from '../services/blindboxService';

const LiquidityPage: React.FC = () => {
  const { walletAddress, userBalance, refreshBalance } = useWallet();
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [poolInfo, setPoolInfo] = useState<PoolInfo | null>(null);
  const [providers, setProviders] = useState<LiquidityProvider[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [addAmount, setAddAmount] = useState<string>('');
  const [removeAmount, setRemoveAmount] = useState<string>('');
  const [userProvider, setUserProvider] = useState<LiquidityProvider | null>(null);
  const toast = useToast();
  const boxBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    fetchLiquidityData();
    fetchBlindboxStats();
  }, [walletAddress]);

  const fetchLiquidityData = async () => {
    if (!walletAddress) return;

    try {
      setLoading(true);
      const result = await liquidityService.getProviders();
      setProviders(result.providers);
      setPoolInfo(result.poolInfo);

      // Check if the current user is a provider
      const currentProvider = result.providers.find(
        (provider) => provider.address === walletAddress
      );
      setUserProvider(currentProvider || null);
    } catch (err) {
      console.error('Error fetching liquidity data:', err);
      setError('Failed to load liquidity data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlindboxStats = async () => {
    try {
      setStatsLoading(true);
      const stats = await blindboxService.getBlindboxStats();
      setTransactions(stats.recentTransactions);
    } catch (err) {
      console.error('Error fetching blindbox stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleAddLiquidity = async () => {
    if (!walletAddress) {
      toast({
        title: 'Wallet not connected',
        description: 'Please connect your wallet first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const amount = parseFloat(addAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Invalid amount',
        description: 'Please enter a valid amount greater than 0.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (amount > userBalance) {
      toast({
        title: 'Insufficient balance',
        description: `You need at least ${amount} SOL to provide this much liquidity.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      await liquidityService.addLiquidity(walletAddress, amount);
      await refreshBalance();
      await fetchLiquidityData();
      await fetchBlindboxStats();
      
      setAddAmount('');
      
      toast({
        title: 'Liquidity added',
        description: `Successfully added ${amount} SOL to the liquidity pool.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Error adding liquidity:', err);
      toast({
        title: 'Failed to add liquidity',
        description: 'There was an error processing your request.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLiquidity = async () => {
    if (!walletAddress || !userProvider) {
      toast({
        title: 'Not a liquidity provider',
        description: 'You need to add liquidity first before removing.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const amount = parseFloat(removeAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Invalid amount',
        description: 'Please enter a valid amount greater than 0.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (amount > userProvider.amount) {
      toast({
        title: 'Insufficient liquidity',
        description: `You only have ${userProvider.amount.toFixed(2)} SOL in the pool.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      await liquidityService.removeLiquidity(walletAddress, amount);
      await refreshBalance();
      await fetchLiquidityData();
      await fetchBlindboxStats();
      
      setRemoveAmount('');
      
      toast({
        title: 'Liquidity removed',
        description: `Successfully removed liquidity from the pool.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Error removing liquidity:', err);
      toast({
        title: 'Failed to remove liquidity',
        description: 'There was an error processing your request.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClaimRewards = async () => {
    if (!walletAddress || !userProvider || userProvider.rewards <= 0) {
      toast({
        title: 'No rewards to claim',
        description: 'You do not have any rewards available to claim.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      await liquidityService.claimRewards(walletAddress);
      await refreshBalance();
      await fetchLiquidityData();
      await fetchBlindboxStats();
      
      toast({
        title: 'Rewards claimed',
        description: `Successfully claimed your rewards.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Error claiming rewards:', err);
      toast({
        title: 'Failed to claim rewards',
        description: 'There was an error processing your request.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !poolInfo) {
    return (
      <Flex justifyContent="center" alignItems="center" minH="50vh">
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Heading as="h1" mb={6}>
        Liquidity Pool
      </Heading>

      {/* Pool Stats */}
      <Box mb={8}>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
          <StatsCard
            title="Total Pool Size"
            stat={`${poolInfo?.totalPoolSize.toFixed(2) || 0} SOL`}
            helpText={`${poolInfo?.growth.toFixed(2) || 0} SOL growth`}
            type={poolInfo?.growth && poolInfo.growth >= 0 ? 'increase' : 'decrease'}
            icon={<FaCoins size="3em" />}
          />
          
          <StatsCard
            title="Pool Mode"
            stat={poolInfo?.state || 'Normal'}
            helpText={`${poolInfo?.totalProviders || 0} providers`}
            icon={<FaUsers size="3em" />}
          />
          
          <StatsCard
            title="Reward Pool"
            stat={`${poolInfo?.rewardPool.toFixed(2) || 0} SOL`}
            helpText="50% of pool growth"
            icon={<FaChartLine size="3em" />}
          />
          
          <StatsCard
            title="Your Contribution"
            stat={`${userProvider?.amount.toFixed(2) || 0} SOL`}
            helpText={userProvider ? `${userProvider.rewards.toFixed(2)} SOL rewards` : 'Not a provider'}
            icon={<FaMoneyBillWave size="3em" />}
          />
        </SimpleGrid>
      </Box>

      <Tabs variant="enclosed" colorScheme="brand">
        <TabList>
          <Tab>Manage Liquidity</Tab>
          <Tab>View Providers</Tab>
          <Tab>Recent Transactions</Tab>
        </TabList>

        <TabPanels>
          {/* Manage Liquidity Tab */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {/* Add Liquidity */}
              <Box 
                bg={boxBg} 
                p={6} 
                borderRadius="lg" 
                boxShadow="sm"
              >
                <Heading size="md" mb={4}>
                  Add Liquidity
                </Heading>
                <Text mb={4} color="gray.500">
                  Provide liquidity to the pool and earn rewards from the pool's growth.
                </Text>
                
                <FormControl mb={4}>
                  <FormLabel>Amount (SOL)</FormLabel>
                  <Input
                    type="number"
                    value={addAmount}
                    onChange={(e) => setAddAmount(e.target.value)}
                    placeholder="Enter amount in SOL"
                    disabled={!walletAddress || loading}
                  />
                  <FormHelperText>
                    Your balance: {userBalance.toFixed(2)} SOL
                  </FormHelperText>
                </FormControl>
                
                <Button
                  colorScheme="brand"
                  isLoading={loading}
                  onClick={handleAddLiquidity}
                  isDisabled={!walletAddress || parseFloat(addAmount) <= 0 || parseFloat(addAmount) > userBalance}
                  w="full"
                >
                  Add Liquidity
                </Button>
              </Box>

              {/* Remove Liquidity & Claim Rewards */}
              <Box 
                bg={boxBg} 
                p={6} 
                borderRadius="lg" 
                boxShadow="sm"
              >
                <Heading size="md" mb={4}>
                  Remove Liquidity
                </Heading>
                <Text mb={4} color="gray.500">
                  {userProvider
                    ? `You have ${userProvider.amount.toFixed(2)} SOL in the pool.`
                    : 'You need to add liquidity first before you can remove.'}
                </Text>
                
                <FormControl mb={4}>
                  <FormLabel>Amount (SOL)</FormLabel>
                  <Input
                    type="number"
                    value={removeAmount}
                    onChange={(e) => setRemoveAmount(e.target.value)}
                    placeholder="Enter amount in SOL"
                    disabled={!userProvider || loading}
                  />
                  <FormHelperText>
                    {userProvider
                      ? `Maximum: ${userProvider.amount.toFixed(2)} SOL`
                      : 'No liquidity provided yet'}
                  </FormHelperText>
                </FormControl>
                
                <Button
                  colorScheme="red"
                  isLoading={loading}
                  onClick={handleRemoveLiquidity}
                  isDisabled={!userProvider || parseFloat(removeAmount) <= 0 || (userProvider && parseFloat(removeAmount) > userProvider.amount)}
                  w="full"
                  mb={6}
                >
                  Remove Liquidity
                </Button>

                <Divider my={4} />

                <Heading size="md" mb={4}>
                  Claim Rewards
                </Heading>
                <HStack justify="space-between" mb={4}>
                  <Text color="gray.500">Available rewards:</Text>
                  <Badge colorScheme="green" fontSize="md" px={2} py={1}>
                    {userProvider?.rewards.toFixed(2) || 0} SOL
                  </Badge>
                </HStack>
                
                <Button
                  colorScheme="brand"
                  variant="outline"
                  isLoading={loading}
                  onClick={handleClaimRewards}
                  isDisabled={!userProvider || userProvider.rewards <= 0}
                  w="full"
                >
                  Claim Rewards
                </Button>
              </Box>
            </SimpleGrid>
          </TabPanel>

          {/* View Providers Tab */}
          <TabPanel>
            <Box 
              bg={boxBg} 
              p={6} 
              borderRadius="lg" 
              boxShadow="sm"
            >
              <Heading size="md" mb={4}>
                Liquidity Providers ({providers.length})
              </Heading>
              
              {providers.length > 0 ? (
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Provider</Th>
                      <Th>Amount</Th>
                      <Th>Share</Th>
                      <Th>Rewards</Th>
                      <Th>Joined</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {providers.map((provider) => (
                      <Tr key={provider.id} bg={provider.address === walletAddress ? 'brand.50' : undefined}>
                        <Td>
                          {provider.address === walletAddress ? (
                            <Badge colorScheme="brand">You</Badge>
                          ) : (
                            <Text fontSize="sm" fontFamily="monospace">
                              {provider.address.substring(0, 6)}...{provider.address.substring(provider.address.length - 4)}
                            </Text>
                          )}
                        </Td>
                        <Td>{provider.amount.toFixed(2)} SOL</Td>
                        <Td>
                          {((provider.amount / (poolInfo?.initialPoolSize || 1)) * 100).toFixed(2)}%
                        </Td>
                        <Td>{provider.rewards.toFixed(2)} SOL</Td>
                        <Td>{new Date(provider.timestamp).toLocaleDateString()}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  No liquidity providers yet. Be the first one!
                </Alert>
              )}
            </Box>
          </TabPanel>

          {/* Transactions Tab */}
          <TabPanel>
            {statsLoading ? (
              <Flex justify="center" py={8}>
                <Spinner />
              </Flex>
            ) : (
              <TransactionList
                transactions={transactions.filter(tx => 
                  tx.type === 'liquidity_add' || 
                  tx.type === 'liquidity_remove' || 
                  tx.type === 'rewards_distribution' ||
                  tx.type === 'rewards_claim'
                )}
                title="Liquidity Transactions"
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Container>
  );
};

export default LiquidityPage; 